'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { useId } from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import type { Realty } from '@eliadmin/core/src/types/realty';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { formatRealtyPrice } from '../../../utils/realty-price';
import { RealtyBadge } from './realty-badge';
import styles from './realty-card.module.css';

interface RealtyCardProps {
  realty: Realty;
  preload?: boolean;
  prefetch?: boolean;
  prefetchOnHover?: boolean;
  className?: {
    container?: string;
    image?: string;
    contentContainer?: string;
    badgesContainer?: string;
    name?: string;
    price?: string;
    divider?: string;
    button?: string;
  };
}

type NamedRealtyValue =
  | string
  | null
  | undefined
  | {
      name?: string | null;
    };

type RealtyWithRuntimeRelations = Realty & {
  offer_type_id?: number | string | null;
  realty_area?: number | string | null;
  realty_city?: NamedRealtyValue;
  realty_village?: NamedRealtyValue;
  type?: NamedRealtyValue;
};

const getRealtyLabel = (value: unknown) => {
  if (typeof value === 'string') return value.trim();
  if (!value || typeof value !== 'object' || !('name' in value)) return '';

  const name = (value as { name?: unknown }).name;
  return typeof name === 'string' ? name.trim() : '';
};

const RealtyLocationIcon = () => (
  <svg
    aria-hidden="true"
    className="size-3 shrink-0"
    focusable="false"
    viewBox="0 0 12 12"
    data-eli-part="location-icon"
  >
    <path
      d="M3.774 8.50675C4.75217 7.63108 5.50071 6.79117 6.01963 5.987C6.53854 5.18283 6.798 4.4785 6.798 3.874C6.798 2.9625 6.50842 2.21317 5.92925 1.626C5.35008 1.03883 4.63167 0.74525 3.774 0.74525C2.91633 0.74525 2.19792 1.03883 1.61875 1.626C1.03958 2.21317 0.75 2.9625 0.75 3.874C0.75 4.4785 1.00946 5.18283 1.52838 5.987C2.04729 6.79117 2.79583 7.63108 3.774 8.50675ZM3.774 9.22012C3.67658 9.22012 3.57917 9.20333 3.48175 9.16975C3.38425 9.13608 3.29608 9.084 3.21725 9.0135C2.76858 8.6 2.34871 8.17417 1.95763 7.736C1.56663 7.29792 1.22675 6.85996 0.938 6.42213C0.649167 5.98429 0.420625 5.55033 0.252375 5.12025C0.084125 4.69008 0 4.27467 0 3.874C0 2.72017 0.373208 1.78604 1.11962 1.07162C1.86612 0.357208 2.75092 0 3.774 0C4.79708 0 5.68187 0.357208 6.42837 1.07162C7.17479 1.78604 7.548 2.72017 7.548 3.874C7.548 4.27467 7.46387 4.68925 7.29562 5.11775C7.12737 5.54633 6.89967 5.98033 6.6125 6.41975C6.32525 6.85917 5.98612 7.29713 5.59512 7.73363C5.20412 8.17021 4.78425 8.59521 4.3355 9.00863C4.25783 9.07913 4.16954 9.132 4.07063 9.16725C3.97179 9.2025 3.87292 9.22012 3.774 9.22012ZM3.77487 4.68262C4.02362 4.68262 4.23629 4.59404 4.41287 4.41688C4.58954 4.23971 4.67787 4.02675 4.67787 3.778C4.67787 3.52925 4.58929 3.31654 4.41212 3.13988C4.23496 2.96329 4.02196 2.875 3.77312 2.875C3.52437 2.875 3.31171 2.96358 3.13513 3.14075C2.95846 3.31792 2.87013 3.53092 2.87013 3.77975C2.87013 4.0285 2.95871 4.24117 3.13588 4.41775C3.31304 4.59433 3.52604 4.68262 3.77487 4.68262Z"
      fill="currentColor"
      transform="translate(2.226 1.25)"
    />
  </svg>
);

export const RealtyCard = ({
  realty,
  preload = false,
  prefetch = false,
  prefetchOnHover = false,
  className,
}: RealtyCardProps) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const runtimeRealty = realty as RealtyWithRuntimeRelations;
  const href = realty.slug ? ROUTES.REALTY({ slug: realty.slug }) : ROUTES.REALTIES();
  const offerLabel = getRealtyLabel(realty.offer_type);
  const typeLabel = getRealtyLabel(runtimeRealty.type);
  const villageLabel = getRealtyLabel(runtimeRealty.realty_village);
  const cityLabel = getRealtyLabel(runtimeRealty.realty_city);
  const locationLabel = villageLabel || cityLabel;
  const numericArea = Number(runtimeRealty.realty_area);
  const hasArea = Number.isFinite(numericArea) && numericArea > 0;
  const formattedPrice = formatRealtyPrice({
    price: realty.price,
    locale,
    currency,
    currencySettings,
  });
  // Home renders the same listing in its mobile and desktop layouts.
  const titleId = `realty-card-title-${realty.id}-${useId()}`;

  const badges = [
    offerLabel
      ? {
          key: 'offer',
          label: offerLabel,
          offerType: runtimeRealty.offer_type_id ?? realty.offer_type,
        }
      : null,
    typeLabel ? { key: 'type', label: typeLabel } : null,
    hasArea ? { key: 'area', label: `${numericArea} m²` } : null,
    locationLabel
      ? {
          key: 'location',
          label: locationLabel,
          icon: <RealtyLocationIcon />,
        }
      : null,
  ].filter((badge): badge is NonNullable<typeof badge> => badge !== null);

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        'ea-realty-card__container flex min-h-[514px] w-full min-w-0 flex-col items-start justify-start gap-4 overflow-hidden rounded-[16px] bg-card p-4 text-foreground',
        'xl:min-h-[232px] xl:flex-row xl:items-center xl:justify-start xl:gap-8 xl:py-4 xl:pl-4 xl:pr-8',
        styles.root,
        className?.container,
      )}
      data-eli-component="realty-card"
      data-realty-id={realty.id}
      data-testid="realty-card"
    >
      <Link
        prefetch={prefetch}
        prefetchOnHover={prefetchOnHover}
        href={href}
        className="relative aspect-[326/257] max-h-[350px] w-full shrink-0 overflow-hidden rounded-[16px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card xl:aspect-auto xl:h-[200px] xl:w-[253.704px] xl:max-w-none"
        data-eli-part="image-link"
      >
        <Image
          src={realty.image?.url || '/images/placeholder.png'}
          alt={resolveImageAlt(realty?.image, realty?.name, realty?.name || '')}
          fill
          preload={preload}
          quality={75}
          unoptimized={realty.image?.url?.startsWith('/eliadmin-preview/')}
          sizes="(max-width: 1279px) calc(100vw - 64px), 254px"
          className={cn(
            'ea-realty-card__image rounded-[16px] object-cover object-center',
            className?.image,
          )}
        />
      </Link>

      <div
        className={cn(
          'ea-property-card__content-container flex min-h-[139px] w-full min-w-0 flex-col items-start gap-4 xl:min-h-0 xl:flex-1',
          className?.contentContainer,
        )}
        data-eli-part="content"
      >
        {badges.length > 0 && (
          <div
            className={cn(
              'ea-property-card__badges-container flex w-full min-w-0 flex-wrap items-start gap-1',
              className?.badgesContainer,
            )}
            data-eli-part="badges"
          >
            {badges.map(badge => (
              <RealtyBadge
                key={badge.key}
                offer_type={badge.offerType}
                label={badge.label}
                badge_type="preview"
                icon={badge.icon}
              />
            ))}
          </div>
        )}

        <h3
          id={titleId}
          className={cn(
            'ea-realty-card__name line-clamp-2 h-[43.2px] w-full translate-y-px text-[18px] font-bold leading-[1.2] text-foreground xl:h-[57.6px] xl:translate-y-0 xl:text-[24px]',
            className?.name,
          )}
          data-eli-part="title"
        >
          <Link
            prefetch={prefetch}
            prefetchOnHover={prefetchOnHover}
            href={href}
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            data-eli-part="title-link"
          >
            {realty.name}
          </Link>
        </h3>

        <div
          className={cn(
            'ea-realty-card__divider h-px w-14 shrink-0 translate-y-px bg-border',
            className?.divider,
          )}
          data-eli-part="divider"
        />

        <span
          className={cn(
            'ea-realty-card__price w-full translate-y-[2px] text-[18px] font-bold leading-[1.2] text-foreground xl:translate-y-px xl:text-[24px]',
            className?.price,
          )}
          data-eli-part="price"
        >
          {formattedPrice}
        </span>
      </div>

      <Button
        asChild
        size="lg"
        className={cn(
          'ea-realty-card__button h-[54px] w-full shrink-0 rounded-[16px] bg-primary px-8 py-[15px] text-[16px] font-semibold !leading-[1.4] text-primary-foreground shadow-none transition-colors hover:!bg-[#0F3005] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card motion-reduce:transition-none motion-reduce:duration-0 xl:w-[190px]',
          styles.cta,
          className?.button,
        )}
      >
        <Link
          prefetch={prefetch}
          prefetchOnHover={prefetchOnHover}
          href={href}
          aria-label={`${t('text1392')}: ${realty.name}`}
          data-eli-part="cta"
          data-testid="realty-card-cta"
        >
          {t('text1392')}
        </Link>
      </Button>
    </article>
  );
};
